import { create } from 'zustand'
import { chatApi, CHAT_REACTION_EMOJIS } from '@/api/chat'
import i18n from '@/i18n/config'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import type {
  ChatContact,
  ChatMessage,
  ChatReactionAggregate,
  ChatRoom,
  SendMessagePayload,
} from '@/api/chat'

interface RoomMessagesState {
  messages: ChatMessage[]
  hasMore: boolean
  nextBeforeId: number | null
  loaded: boolean
}

interface ChatState {
  rooms: ChatRoom[]
  contacts: ChatContact[]
  currentRoomId: number | null
  byRoom: Record<number, RoomMessagesState>
  isLoadingRooms: boolean
  isLoadingMessages: boolean
  isLoadingContacts: boolean
  error: string | null

  fetchRooms: () => Promise<void>
  fetchContacts: () => Promise<void>
  openDirect: (userId: number) => Promise<ChatRoom>
  setCurrentRoom: (roomId: number | null) => void
  fetchMessages: (roomId: number, options?: { loadMore?: boolean }) => Promise<void>
  sendMessage: (roomId: number, payload: SendMessagePayload) => Promise<ChatMessage>
  editMessage: (roomId: number, messageId: number, payload: SendMessagePayload) => Promise<void>
  deleteMessage: (roomId: number, messageId: number) => Promise<void>
  markRead: (roomId: number, upTo: number) => Promise<void>
  setRoomMuted: (roomId: number, isMuted: boolean) => void
  updateRoom: (room: ChatRoom) => void
  toggleReaction: (
    roomId: number,
    messageId: number,
    emoji: string,
    currentUserId: number
  ) => Promise<void>

  applyIncomingMessage: (message: ChatMessage) => void
  applyUpdatedMessage: (message: ChatMessage) => void
  applyDeletedMessage: (message: ChatMessage) => void
  applyReadMark: (roomId: number, userId: number, lastReadMessageId: number) => void
  applyReactionUpdate: (
    roomId: number,
    messageId: number,
    reactions: ChatReactionAggregate[],
    version?: number
  ) => void
  applyRealtimeReactionUpdate: (
    roomId: number,
    messageId: number,
    reactions: ChatReactionAggregate[],
    version?: number
  ) => void

  clearError: () => void
}

export const selectHasUnreadMessages = (s: ChatState): boolean =>
  s.rooms.some((r) => r.unread_count > 0)

const emptyRoomState: RoomMessagesState = {
  messages: [],
  hasMore: false,
  nextBeforeId: null,
  loaded: false,
}

/** Очереди toggle-реакций по сообщению — запросы уходят строго по одному. */
const reactionToggleQueues = new Map<number, Promise<void>>()
/** Счётчик in-flight toggle по сообщению: ответ применяется только у последнего. */
const pendingReactionToggles = new Map<number, number>()

function upsertRoom(rooms: ChatRoom[], room: ChatRoom): ChatRoom[] {
  const exists = rooms.some((r) => r.id === room.id)
  return exists ? rooms.map((r) => (r.id === room.id ? room : r)) : [room, ...rooms]
}

export const useChatStore = create<ChatState>((set, get) => ({
  rooms: [],
  contacts: [],
  currentRoomId: null,
  byRoom: {},
  isLoadingRooms: false,
  isLoadingMessages: false,
  isLoadingContacts: false,
  error: null,

  fetchRooms: async () => {
    set({ isLoadingRooms: true, error: null })
    try {
      const rooms = await chatApi.getRooms()
      set({ rooms })
    } catch (error: unknown) {
      set({ error: displayErrorMessage(getErrorMessage(error), i18n.t('common.error')) })
    } finally {
      set({ isLoadingRooms: false })
    }
  },

  fetchContacts: async () => {
    set({ isLoadingContacts: true, error: null })
    try {
      const contacts = await chatApi.getContacts()
      set({ contacts })
    } catch (error: unknown) {
      set({ error: displayErrorMessage(getErrorMessage(error), i18n.t('common.error')) })
    } finally {
      set({ isLoadingContacts: false })
    }
  },

  openDirect: async (userId) => {
    set({ error: null })
    const room = await chatApi.openDirect(userId)
    set((state) => ({ rooms: upsertRoom(state.rooms, room), currentRoomId: room.id }))
    return room
  },

  setCurrentRoom: (roomId) => set({ currentRoomId: roomId }),

  fetchMessages: async (roomId, options) => {
    const loadMore = options?.loadMore ?? false
    const existing = get().byRoom[roomId] ?? emptyRoomState

    if (loadMore && !existing.hasMore) return

    set({ isLoadingMessages: true, error: null })
    try {
      const page = await chatApi.getMessages(roomId, {
        limit: 30,
        before_id: loadMore ? existing.nextBeforeId ?? undefined : undefined,
      })

      set((state) => {
        const prev = state.byRoom[roomId] ?? emptyRoomState
        const merged = loadMore ? [...page.data, ...prev.messages] : page.data
        return {
          byRoom: {
            ...state.byRoom,
            [roomId]: {
              messages: dedupeById(merged),
              hasMore: page.meta.has_more,
              nextBeforeId: page.meta.next_before_id,
              loaded: true,
            },
          },
        }
      })
    } catch (error: unknown) {
      set({ error: displayErrorMessage(getErrorMessage(error), i18n.t('common.error')) })
    } finally {
      set({ isLoadingMessages: false })
    }
  },

  sendMessage: async (roomId, payload) => {
    const message = await chatApi.sendMessage(roomId, payload)
    get().applyIncomingMessage(message)
    return message
  },

  editMessage: async (_roomId, messageId, payload) => {
    const updated = await chatApi.editMessage(messageId, payload)
    get().applyUpdatedMessage(updated)
  },

  deleteMessage: async (roomId, messageId) => {
    await chatApi.deleteMessage(messageId)
    set((state) => patchMessage(state, roomId, messageId, (m) => ({
      ...m,
      is_deleted: true,
      text: null,
      attachments: [],
    })))
  },

  markRead: async (roomId, upTo) => {
    await chatApi.markRead(roomId, upTo)
    set((state) => ({
      rooms: state.rooms.map((r) => (r.id === roomId ? { ...r, unread_count: 0 } : r)),
    }))
  },

  setRoomMuted: (roomId, isMuted) => {
    set((state) => ({
      rooms: state.rooms.map((r) => (r.id === roomId ? { ...r, is_muted: isMuted } : r)),
    }))
  },

  updateRoom: (room) => {
    set((state) => ({
      rooms: upsertRoom(state.rooms, room),
    }))
  },

  // Оптимистичный toggle: агрегат пересчитывается локально сразу, запросы
  // сериализуются очередью по сообщению. Серверные агрегаты применяются через
  // reactions_version (см. applyReactionUpdate) — опоздавший ответ со старой
  // версией отбрасывается сам. Только патч существующего сообщения —
  // временные id недопустимы (dedupeById сортирует по числовому id).
  toggleReaction: async (roomId, messageId, emoji, currentUserId) => {
    const message = get().byRoom[roomId]?.messages.find((m) => m.id === messageId)
    if (!message || message.is_deleted) return

    const optimistic = toggleReactionAggregate(message.reactions ?? [], emoji, currentUserId)
    get().applyReactionUpdate(roomId, messageId, optimistic)

    pendingReactionToggles.set(messageId, (pendingReactionToggles.get(messageId) ?? 0) + 1)

    const run = async () => {
      try {
        const result = await chatApi.toggleReaction(messageId, emoji)
        get().applyReactionUpdate(roomId, messageId, result.reactions, result.reactions_version)
      } catch (error: unknown) {
        // Откат — только если состояние не сменилось более свежим: наш
        // оптимистичный массив всё ещё на месте (сравнение по ссылке).
        // Если уже пришёл авторитетный агрегат — он и есть правда.
        const current = get().byRoom[roomId]?.messages.find((m) => m.id === messageId)
        if (current && !current.is_deleted && current.reactions === optimistic) {
          get().applyReactionUpdate(
            roomId,
            messageId,
            toggleReactionAggregate(optimistic, emoji, currentUserId)
          )
        }
        set({ error: displayErrorMessage(getErrorMessage(error), i18n.t('common.error')) })
      } finally {
        // Счётчики и настроенные очереди не должны копиться бесконечно
        // (module-level Map живёт всю сессию SPA).
        const left = (pendingReactionToggles.get(messageId) ?? 1) - 1
        if (left <= 0) {
          pendingReactionToggles.delete(messageId)
        } else {
          pendingReactionToggles.set(messageId, left)
        }
      }
    }

    const queue = (reactionToggleQueues.get(messageId) ?? Promise.resolve()).then(run)
    reactionToggleQueues.set(messageId, queue)
    await queue.finally(() => {
      if (reactionToggleQueues.get(messageId) === queue) {
        reactionToggleQueues.delete(messageId)
      }
    })
  },

  applyIncomingMessage: (message) => {
    set((state) => {
      const roomState = state.byRoom[message.chat_room_id] ?? emptyRoomState
      const alreadyExists = roomState.messages.some((m) => m.id === message.id)
      const messages = alreadyExists
        ? roomState.messages.map((m) => (m.id === message.id ? message : m))
        : [...roomState.messages, message]

      const isCurrent = state.currentRoomId === message.chat_room_id
      const rooms = state.rooms.map((r) => {
        if (r.id !== message.chat_room_id) return r
        return {
          ...r,
          last_message: message,
          unread_count: isCurrent ? r.unread_count : r.unread_count + 1,
        }
      })

      return {
        byRoom: {
          ...state.byRoom,
          [message.chat_room_id]: { ...roomState, messages: dedupeById(messages), loaded: true },
        },
        rooms,
      }
    })
  },

  applyUpdatedMessage: (message) => {
    set((state) => patchMessage(state, message.chat_room_id, message.id, () => message))
  },

  applyDeletedMessage: (message) => {
    set((state) => patchMessage(state, message.chat_room_id, message.id, (m) => ({
      ...m,
      is_deleted: true,
      text: null,
      attachments: [],
    })))
  },

  // version отсутствует у оптимистичных локальных апдейтов (версию не меняют);
  // серверные (ответ toggle, broadcast) несут монотонную reactions_version —
  // кадр со старой версией отбрасывается (last-writer-wins больше не теряет
  // чужие реакции у наблюдателей).
  applyReactionUpdate: (roomId, messageId, reactions, version) => {
    set((state) =>
      patchMessage(state, roomId, messageId, (m) => {
        if (version !== undefined && (m.reactions_version ?? 0) >= version) {
          return m
        }
        return {
          ...m,
          reactions,
          ...(version !== undefined ? { reactions_version: version } : {}),
        }
      })
    )
  },

  // Realtime-кадры дополнительно глушатся, пока есть свои in-flight toggle:
  // echo собственного действия (событие идёт и отправителю) промежуточным
  // состоянием «мигал» бы поверх оптимистичного UI.
  applyRealtimeReactionUpdate: (roomId, messageId, reactions, version) => {
    if ((pendingReactionToggles.get(messageId) ?? 0) > 0) return
    get().applyReactionUpdate(roomId, messageId, reactions, version)
  },

  applyReadMark: (roomId, userId, lastReadMessageId) => {
    set((state) => ({
      rooms: state.rooms.map((r) => {
        if (r.id !== roomId || !r.members) return r
        return {
          ...r,
          members: r.members.map((member) =>
            member.user_id === userId
              ? { ...member, last_read_message_id: lastReadMessageId }
              : member
          ),
        }
      }),
    }))
  },

  clearError: () => set({ error: null }),
}))

/** Локальный пересчёт агрегата для оптимистичного toggle (порядок — по whitelist). */
function toggleReactionAggregate(
  reactions: ChatReactionAggregate[],
  emoji: string,
  userId: number
): ChatReactionAggregate[] {
  const existing = reactions.find((r) => r.emoji === emoji)

  if (existing?.user_ids.includes(userId)) {
    return reactions
      .map((r) =>
        r.emoji === emoji
          ? { ...r, count: r.count - 1, user_ids: r.user_ids.filter((id) => id !== userId) }
          : r
      )
      .filter((r) => r.count > 0)
  }

  if (existing) {
    return reactions.map((r) =>
      r.emoji === emoji ? { ...r, count: r.count + 1, user_ids: [...r.user_ids, userId] } : r
    )
  }

  const orderOf = (value: string): number => {
    const index = (CHAT_REACTION_EMOJIS as readonly string[]).indexOf(value)
    return index === -1 ? CHAT_REACTION_EMOJIS.length : index
  }

  return [...reactions, { emoji, count: 1, user_ids: [userId] }].sort(
    (a, b) => orderOf(a.emoji) - orderOf(b.emoji)
  )
}

function dedupeById(messages: ChatMessage[]): ChatMessage[] {
  const map = new Map<number, ChatMessage>()
  for (const message of messages) {
    map.set(message.id, message)
  }
  return Array.from(map.values()).sort((a, b) => a.id - b.id)
}

function patchMessage(
  state: ChatState,
  roomId: number,
  messageId: number,
  updater: (message: ChatMessage) => ChatMessage
): Partial<ChatState> {
  const roomState = state.byRoom[roomId]
  if (!roomState) return {}
  return {
    byRoom: {
      ...state.byRoom,
      [roomId]: {
        ...roomState,
        messages: roomState.messages.map((m) => (m.id === messageId ? updater(m) : m)),
      },
    },
  }
}
